import { type ReactElement, useMemo } from 'react';
import {
  i18nInit,
  useLanguageControlOverrides,
  useLoadLocale
} from '@elfsight/lib-i18n';
import { LOCAL_WIDGET_ID } from '@elfsight/lib-utils';
import { FormProvider } from '@elfsight/widget-form-builder/widget';
import { useCustomJS, useDefineAPISchema } from '@elfsight/widget-js-api';
import { PortalNestProvider } from '@elfsight/widget-modal';
import {
  CustomCSS,
  createCustomCSSPrefixClassName,
  useFontsLoader
} from '@elfsight/widget-ui';
import { I18nProvider } from '@lingui/react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { useSettings } from '@wu/widget-core';
import CATALOG from '../i18n/languages/widget/catalog.json';
import type { AppointmentBookingSettings } from './types';
import { CurrentTimeZoneProvider } from './widget/current-timezone-provider';
import { DatetimeProvider } from './widget/datetime/datetime-provider';
import { EventProvider } from './widget/event-provider';
import { PaymentDataProvider } from './widget/payment/payment-data-provider';
import { ServicesProvider } from './widget/service/services-provider';
import { Widget } from './widget/widget';
import { WidgetDimensionsProvider } from './widget/widget-dimensions-provider';

const queryClient = new QueryClient({});

export function AppointmentBooking(_settings: AppointmentBookingSettings) {
  return (
    <EventProvider>
      <QueryClientProvider client={queryClient}>
        <CurrentTimeZoneProvider>
          <AppointmentBookingProviders>
            <Widget />
          </AppointmentBookingProviders>
        </CurrentTimeZoneProvider>
      </QueryClientProvider>
    </EventProvider>
  );
}

function AppointmentBookingProviders({ children }: { children: ReactElement }) {
  const {
    customCSS,
    customJS,
    font,
    widgetId,
    language,
    editMode,
    renderThumbnailMode
  } = useSettings();

  const customCSSPrefixClassName = createCustomCSSPrefixClassName(
    'appointment-booking',
    widgetId
  );

  const i18n = useMemo(() => i18nInit(), []);

  useDefineAPISchema();
  useCustomJS(customJS, editMode || renderThumbnailMode);
  useFontsLoader([font]);

  const messages = useLoadLocale(language?.language, 'widget', i18n);
  useLanguageControlOverrides({ language, messages, catalog: CATALOG, i18n });

  return (
    <I18nProvider i18n={i18n}>
      <ReactQueryDevtools buttonPosition="bottom-left" position="left" />
      <PortalNestProvider
        customCSSClassName={customCSSPrefixClassName}
        font={font}
      >
        <CustomCSS
          customCSS={customCSS}
          prefixClassName={customCSSPrefixClassName}
        />
        <ServicesProvider>
          <DatetimeProvider>
            <FormProvider widgetId={widgetId || LOCAL_WIDGET_ID}>
              <WidgetDimensionsProvider>
                <PaymentDataProvider>{children}</PaymentDataProvider>
              </WidgetDimensionsProvider>
            </FormProvider>
          </DatetimeProvider>
        </ServicesProvider>
      </PortalNestProvider>
    </I18nProvider>
  );
}
